import { obsMetricNames } from "@/resources/observations_metricUtils";
import { Consciousness, Observation } from "@/resources/types";
import { vitalsSignsTableSections, ObsInputRange, ObsInputString, bedsideTableSections, ventilatorTableSections, ObsInputOriginValue } from "./index";


const respirationRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.respirations)?.inputs || []
) as ObsInputRange[];
const o2SatsRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.o2Sats)?.inputs || []
) as ObsInputRange[];
const o2FlowRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.o2Flow)?.inputs || []
) as ObsInputRange[];
const heartRateRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.heartRate)?.inputs || []
) as ObsInputRange[];
const bloodPressureRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.bloodPressure)?.inputs || []
) as ObsInputRange[];
const temperatureRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.temperature)?.inputs || []
) as ObsInputRange[];
const consciousnessStrings = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.consciousness)?.inputs || []
) as ObsInputString[];
const bloodGlucoseRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.bloodGlucose)?.inputs || []
) as ObsInputOriginValue[];
const bloodAlcoholRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.bloodAlcohol)?.inputs || []
) as ObsInputOriginValue[];
const painScoreRanges = (
  vitalsSignsTableSections.find(it => it.dataKey === obsMetricNames.painScore)?.inputs || []
) as ObsInputOriginValue[];

//bedside
const bedsideBloodPressureLeftArmRanges = (
  bedsideTableSections.find(it => it.dataKey === obsMetricNames.bedsideBloodPressureLeftArm)?.inputs || []
) as ObsInputOriginValue[];
const bedsideBloodPressureRightArmRanges = (
  bedsideTableSections.find(it => it.dataKey === obsMetricNames.bedsideBloodPressureRightArm)?.inputs || []
) as ObsInputOriginValue[];
const bedsideBloodPressureLayingRanges = (
  bedsideTableSections.find(it => it.dataKey === obsMetricNames.bedsideBloodPressureLaying)?.inputs || []
) as ObsInputOriginValue[];
const bedsideBloodPressureStandingRanges = (
  bedsideTableSections.find(it => it.dataKey === obsMetricNames.bedsideBloodPressureStanding)?.inputs || []
) as ObsInputOriginValue[];
const bedsideHeartRateRanges = (
  bedsideTableSections.find(it => it.dataKey === obsMetricNames.bedsideHeartRate)?.inputs || []
) as ObsInputOriginValue[];

//ventilator
const ventilatorTidalVolumeRanges = (
  ventilatorTableSections.find(it => it.dataKey === obsMetricNames.ventilatorTidalVolume)?.inputs || []
) as ObsInputOriginValue[];
const ventilatorMinuteVentilationRanges = (
  ventilatorTableSections.find(it => it.dataKey === obsMetricNames.ventilatorMinuteVentilation)?.inputs || []
) as ObsInputOriginValue[];
const ventilatorFrequencyRateRanges = (
  ventilatorTableSections.find(it => it.dataKey === obsMetricNames.ventilatorFrequencyRate)?.inputs || []
) as ObsInputOriginValue[];
const ventilatorPeakPressureRanges = (
  ventilatorTableSections.find(it => it.dataKey === obsMetricNames.ventilatorPeakPressure)?.inputs || []
) as ObsInputOriginValue[];
const ventilatorEndTidalCarbonDioxideRanges = (
  ventilatorTableSections.find(it => it.dataKey === obsMetricNames.ventilatorEndTidalCarbonDioxide)?.inputs || []
) as ObsInputOriginValue[];

/**
 * - 1 - very bad (low)
 * - 2 - bad (low)
 * - 3 - good
 * - 4 - good
 * - 5 - good
 * - 6 - bad (high)
 * - 7 - very bad (high)
 *
 * we have three copies of "good" to make it more visible\
 * and evident in the line chart
 */
export type Mark = 1 | 2 | 3 | 4 | 5 | 6 | 7;


const deriveMarkFromNewsAndTooHigh = (r: ObsInputRange): Mark => {
  const { news, tooHigh } = r;

  if (news === undefined) return 4;

  if (tooHigh) return news === 1 ? 6 : 7;

  return news === 1 ? 2 : 1;
};

type Bedside = NonNullable<Observation["bedside"]>;
type BedsideBloodPressure = NonNullable<Bedside["bloodPressure"]>;
type Ventilator = NonNullable<Observation["ventilator"]>;
type Urinalysis = NonNullable<Observation["urinalysis"]>;
// eslint-disable-next-line complexity
export const obsMetricToMark = (
  arg: (
    | { type: typeof obsMetricNames.respirations, value: Observation["respirations"] }
    | { type: typeof obsMetricNames.o2Sats, value: Observation["o2Sats"] }
    | { type: typeof obsMetricNames.o2Flow, value: Observation["o2Flow"] }
    | { type: typeof obsMetricNames.heartRate, value: Observation["heartRate"] }
    | { type: typeof obsMetricNames.bloodPressure, value: Observation["bloodPressure"] }
    | { type: typeof obsMetricNames.temperature, value: Observation["temperature"] }
    | { type: typeof obsMetricNames.consciousness, value: Observation["consciousness"] }
    | { type: typeof obsMetricNames.bloodGlucose, value: Observation["bloodGlucose"] }
    | { type: typeof obsMetricNames.bloodAlcohol, value: Observation["bloodAlcohol"] }
    // | { type: typeof obsMetricNames.riskScore, value: Observation[ "riskScore" ] }
    | { type: typeof obsMetricNames.painScore, value: Observation["painScore"] }
    | { type: typeof obsMetricNames.bedsideBloodPressureLeftArm, value: BedsideBloodPressure["leftArm"]; }
    | { type: typeof obsMetricNames.bedsideBloodPressureRightArm, value: BedsideBloodPressure["rightArm"] }
    | { type: typeof obsMetricNames.bedsideBloodPressureLaying, value: BedsideBloodPressure["laying"] }
    | { type: typeof obsMetricNames.bedsideBloodPressureStanding, value: BedsideBloodPressure["standing"] }
    | { type: typeof obsMetricNames.bedsideHeartRate, value: Bedside["heartRate"] }
    | { type: typeof obsMetricNames.ventilatorTidalVolume, value: Ventilator["tidalVolume"] }
    | { type: typeof obsMetricNames.ventilatorMinuteVentilation, value: Ventilator["minuteVentilation"] }
    | { type: typeof obsMetricNames.ventilatorFrequencyRate, value: Ventilator["frequencyRate"] }
    | { type: typeof obsMetricNames.ventilatorPeakPressure, value: Ventilator["peakPressure"] }
    | { type: typeof obsMetricNames.ventilatorEndTidalCarbonDioxide, value: Ventilator["endTidalCarbonDioxide"] }
    | { type: typeof obsMetricNames.urinalysisAbnormalities, value: Urinalysis["abnormalities"] }
    | { type: typeof obsMetricNames.urinalysisDistream, value: Urinalysis["distream"] }
    | { type: typeof obsMetricNames.urinalysisPregnancy, value: Urinalysis["pregnancy"] }
    | { type: typeof obsMetricNames.urinalysisGlucose, value: Urinalysis["glucose"] }
    | { type: typeof obsMetricNames.urinalysisProtein, value: Urinalysis["protein"] }
    | { type: typeof obsMetricNames.urinalysisBlood, value: Urinalysis["blood"] }
    | { type: typeof obsMetricNames.urinalysisBilirubin, value: Urinalysis["bilirubin"] }
    | { type: typeof obsMetricNames.urinalysisNitrite, value: Urinalysis["nitrite"] }
    | { type: typeof obsMetricNames.urinalysisUrobilinogen, value: Urinalysis["urobilinogen"] }
    | { type: typeof obsMetricNames.urinalysisLeukocytes, value: Urinalysis["leukocytes"] }
    | { type: typeof obsMetricNames.urinalysisPh, value: Urinalysis["ph"] }
    | { type: typeof obsMetricNames.urinalysisKetone, value: Urinalysis["ketone"] }
    | { type: typeof obsMetricNames.urinalysisSpecificGravity, value: Urinalysis["specificGravity"] }
  )
): Mark | undefined => {
  switch (arg.type) {
    case obsMetricNames.respirations: {
      const { value } = arg;

      const range = value !== undefined && respirationRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.o2Sats: {
      const { value } = arg;

      const range = value !== undefined && o2SatsRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.o2Flow: {
      const { value } = arg;

      const range = value !== undefined && o2FlowRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.heartRate: {
      const { value } = arg;

      const range = value !== undefined && heartRateRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bloodPressure: {
      const { value } = arg;

      const range = value !== undefined && bloodPressureRanges
        .find(({ range: { min, max } }) => min <= value.systolic && max >= value.systolic);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.temperature: {
      const { value } = arg;

      const range = value !== undefined && temperatureRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.consciousness: {
      const { value } = arg;
      if (value === Consciousness.NA) return undefined;

      const range = value !== undefined && consciousnessStrings.find(it => it.value === value);
      if (!range) return undefined;

      const { news } = range;

      if (news == undefined) return 4;
      return news === 1 ? 2 : 1;
    }
    case obsMetricNames.bloodGlucose: {
      const { value } = arg;

      const range = value !== undefined && bloodGlucoseRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bloodAlcohol: {
      const { value } = arg;

      const range = value !== undefined && bloodAlcoholRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.painScore: {
      const { value } = arg;

      const range = value !== undefined && painScoreRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bedsideBloodPressureLeftArm: {
      const { value } = arg;

      const range = value !== undefined && value.systolic !== undefined
        && bedsideBloodPressureLeftArmRanges.find(({ range: { min, max } }) => min <= (value.systolic || 0) && max >= (value.systolic || 0));
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bedsideBloodPressureRightArm: {
      const { value } = arg;

      const range = value !== undefined && value.systolic !== undefined
        && bedsideBloodPressureRightArmRanges.find(({ range: { min, max } }) => min <= (value.systolic || 0) && max >= (value.systolic || 0));
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bedsideBloodPressureLaying: {
      const { value } = arg;

      const range = value !== undefined && value.systolic !== undefined
        && bedsideBloodPressureLayingRanges.find(({ range: { min, max } }) => min <= (value.systolic || 0) && max >= (value.systolic || 0));
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bedsideBloodPressureStanding: {
      const { value } = arg;

      const range = value !== undefined && value.systolic !== undefined
        && bedsideBloodPressureStandingRanges.find(({ range: { min, max } }) => min <= (value.systolic || 0) && max >= (value.systolic || 0));
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.bedsideHeartRate: {
      const { value } = arg;

      const range = value !== undefined && bedsideHeartRateRanges.find(({ range: { min, max } }) => min <= Number(value) && max >= Number(value));
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.ventilatorTidalVolume: {
      const { value } = arg;

      const range = value !== undefined && ventilatorTidalVolumeRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.ventilatorMinuteVentilation: {
      const { value } = arg;

      const range = value !== undefined && ventilatorMinuteVentilationRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.ventilatorFrequencyRate: {
      const { value } = arg;

      const range = value !== undefined && ventilatorFrequencyRateRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.ventilatorPeakPressure: {
      const { value } = arg;

      const range = value !== undefined && ventilatorPeakPressureRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    case obsMetricNames.ventilatorEndTidalCarbonDioxide: {
      const { value } = arg;

      const range = value !== undefined && ventilatorEndTidalCarbonDioxideRanges.find(({ range: { min, max } }) => min <= value && max >= value);
      if (!range) return undefined;

      return deriveMarkFromNewsAndTooHigh(range);
    }
    default: return undefined;
  }
};
